/**
 * @fileoverview Job-level Key Dates Calendar
 *
 * URL: /dashboard/jobs/:jobId/calendar
 * Protection: requires authentication + job access (via Convex query).
 *
 * Renders the ported big-calendar surface with the job's derived +
 * persisted key dates. Click an event to open the side panel; click an
 * empty cell or "+ Add key date" to create a manual entry pre-filled
 * with the chosen date.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { format } from "date-fns";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { KeyDateCategory } from "@cvx/keyDates/jobKeyDates/schema";

import {
  CalendarProvider,
  ClientContainer,
  KeyDateSidePanel,
  toCalendarEvents,
  type KeyDatePanelMode,
  type TCalendarView,
} from "@/calendar";
import { Spinner } from "@/ui/primitives/spinner";

/**
 * Default category visibility on the calendar surface. Investigation
 * lookback windows are deliberately excluded — they're date ranges,
 * not point events, and live in the dedicated
 * `InvestigationWindowsPanel` on the Review Windows route.
 */
const DEFAULT_VISIBLE_CATEGORIES: KeyDateCategory[] = [
  "appointment",
  "lodgement",
  "meeting",
  "milestone",
  "custom",
];

/**
 * Hide-subtasks toggle, persisted per browser (MOR-752). Shared with the
 * workspace calendar so the preference follows the user across calendars.
 */
const HIDE_SUBTASKS_KEY = "morgan.calendar.hideSubtasks";

function loadHideSubtasks(): boolean {
  try {
    return localStorage.getItem(HIDE_SUBTASKS_KEY) === "true";
  } catch {
    return false;
  }
}

function persistHideSubtasks(value: boolean): void {
  try {
    localStorage.setItem(HIDE_SUBTASKS_KEY, String(value));
  } catch {
    // localStorage unavailable (private mode) — the toggle just won't persist.
  }
}

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/calendar/",
)({
  component: JobCalendarPage,
});

function JobCalendarPage() {
  const { jobId } = Route.useParams();
  const typedJobId = jobId as Id<"jobs">;

  const { data: events } = useQuery(
    convexQuery(api.keyDates.queries.listForJob, { jobId: typedJobId }),
  );
  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: typedJobId }),
  );

  const [view, setView] = useState<TCalendarView>("week");
  const [panel, setPanel] = useState<KeyDatePanelMode>({ kind: "closed" });
  const [visibleCategories, setVisibleCategories] = useState<KeyDateCategory[]>(
    DEFAULT_VISIBLE_CATEGORIES,
  );
  const [hideSubtasks, setHideSubtasks] = useState<boolean>(loadHideSubtasks);
  const handleHideSubtasksChange = (next: boolean) => {
    setHideSubtasks(next);
    persistHideSubtasks(next);
  };

  const calendarEvents = useMemo(
    () => (events ? toCalendarEvents(events) : []),
    [events],
  );

  const handleEventClick = (eventId: string | number) => {
    if (!events) return;
    const row = events.find((e) => e.id === String(eventId));
    if (row) setPanel({ kind: "view", event: row });
  };

  const handleDateCellClick = (date: Date, endDate?: Date) => {
    const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0;
    setPanel({
      kind: "create",
      jobId: typedJobId,
      initialDate: format(date, "yyyy-MM-dd"),
      initialTime: hasTime ? format(date, "HH:mm") : undefined,
      initialEndTime: endDate ? format(endDate, "HH:mm") : undefined,
    });
  };

  if (events === undefined || job === undefined || job === null) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Events</h1>
        <p className="text-sm text-muted-foreground">
          Statutory dates are captured automatically from the job. Add an event
          or task by selecting an empty cell in the calendar or clicking the
          &ldquo;Add new&rdquo; button.
        </p>
      </div>

      <CalendarProvider
        view={view}
        onViewChange={setView}
        events={calendarEvents}
        onEventClick={(event) => handleEventClick(event.id)}
        onDateCellClick={handleDateCellClick}
      >
        <ClientContainer
          visibleCategories={visibleCategories}
          onVisibleCategoriesChange={setVisibleCategories}
          hideSubtasks={hideSubtasks}
          onHideSubtasksChange={handleHideSubtasksChange}
        />
      </CalendarProvider>

      {/* Create / view / edit all live in the side panel (MOR-707 follow-up):
          the CreatePanel owns the Type-aware "Add new" flow. */}
      <KeyDateSidePanel
        state={panel}
        onClose={() => setPanel({ kind: "closed" })}
      />
    </div>
  );
}
